import {
  memo, useEffect, useRef, useState,
} from 'react';
import { useStore, useUpdateNodeInternals } from '@xyflow/react';
import { clsx } from 'clsx';
import TooltipPanel from 'components/TooltipPanel/TooltipPanel';
import { VALUE_CHAIN_MAP_CUSTOM_NODES } from 'constants/interfaces';

import {
  VALUE_CHAIN_MAP,
  VC_SWIMLANE_TITLE_SCALE_ZOOM_THRESHOLD,
} from '../../../../../../constants/general';

import type { ICustomSwimlaneNode } from '../interfaces/ICustomSwimlaneNode';

import styles from '../sass/CustomSwimlaneNode.module.scss';

// Distance from the swimlane top to the title text: container padding (16) + title padding (8)
const SWIMLANE_TITLE_TOP_OFFSET = 24;
// Gap to keep between the scaled-up title and the first unit card below it
const SCALED_TITLE_BOTTOM_GAP = 6;

function CustomSwimlaneNode({ id, data }: ICustomSwimlaneNode) {
  const [isHovered, setIsHovered] = useState(false);
  const updateNodeInternals = useUpdateNodeInternals();
  const titleTextRef = useRef<HTMLSpanElement | null>(null);
  const zoom = useStore((state) => state.transform[2]);
  const calculatedHeight = data.accumulatedMaxBoardHeight - VALUE_CHAIN_MAP.TITLE_HEIGHT
    - VALUE_CHAIN_MAP.BOARD_PADDING;

  // Keep the column title readable while zooming out: scale it up inversely to
  // the zoom level, but never wider than the swimlane itself and never below
  // the top of the first unit card (it sits at TITLE_HEIGHT + UNIT_TOP_PADDING
  // from the swimlane top), so the scaled-up title doesn't overlap the cards
  const getTitleScale = (): number => {
    if (zoom <= 0 || zoom > VC_SWIMLANE_TITLE_SCALE_ZOOM_THRESHOLD) {
      return 1;
    }

    const desiredScale = VC_SWIMLANE_TITLE_SCALE_ZOOM_THRESHOLD / zoom;
    const titleTextWidth = titleTextRef.current?.offsetWidth || 0;
    const titleTextHeight = titleTextRef.current?.offsetHeight || 0;

    if (!titleTextWidth || !titleTextHeight) {
      return desiredScale;
    }

    const availableTitleHeight = VALUE_CHAIN_MAP.TITLE_HEIGHT + VALUE_CHAIN_MAP.UNIT_TOP_PADDING
      - SWIMLANE_TITLE_TOP_OFFSET - SCALED_TITLE_BOTTOM_GAP;
    const maxFitScale = (VALUE_CHAIN_MAP.SWIMLANE_WIDTH - VALUE_CHAIN_MAP.UNIT_HORIZONTAL_PADDING) / titleTextWidth;
    const maxVerticalFitScale = availableTitleHeight / titleTextHeight;

    return Math.max(1, Math.min(desiredScale, maxFitScale, maxVerticalFitScale));
  };

  const titleScale = getTitleScale();
  const isTitleScaledUp = titleScale > 1;

  useEffect(() => {
    updateNodeInternals(id);
  }, [
    calculatedHeight,
    data.draft,
    data.isDraftReviewMode,
    data.name,
    id,
    updateNodeInternals,
  ]);

  return (
    <div
      onMouseLeave={() => setIsHovered(false)}
      className={clsx(styles.container, {
        [styles.dragTarget]: data.isDragTarget,
        [styles.draftReviewActive]: data.isDraftReviewMode && data.draft,
        [styles.draftReviewDimmed]: data.isDraftReviewMode && !data.draft,
        [styles.scenarioViewMode]: data.isScenarioViewMode,
      })}
      style={{ height: calculatedHeight || 'auto' }}
    >
      <TooltipPanel
        onEdit={() => data.onEditSwimlane(data)}
        onDelete={() => data.onDeleteSwimlane(data)}
        hideDeleteButton={data.type === VALUE_CHAIN_MAP_CUSTOM_NODES.CUSTOM}
        isAccessDisabled={data.isAccessDisabled}
        isHovered={isHovered}
      >
        <p
          className={clsx(styles.title, 'nopan', 'react-flow__node-drag-handle', {
            [styles.isHovered]: isHovered && !data.isAccessDisabled,
          })}
          onMouseEnter={() => setIsHovered(true)}
        >
          <span
            ref={titleTextRef}
            className={clsx(styles.titleText, {
              [styles.titleTextScaledUp]: isTitleScaledUp,
            })}
            style={isTitleScaledUp ? {
              transform: `scale(${titleScale})`,
              transformOrigin: 'center top',
            } : undefined}
          >
            {data.name}
          </span>
        </p>
      </TooltipPanel>
    </div>
  );
}

export default memo(CustomSwimlaneNode);
